import { Nullable, NullableResultPromise } from "../base-types";
import { ResponseHandler } from "../clients/response-handler";
import { DEFINITIONS } from "../constants";
import { IEntity } from "../contracts/base/entity";
import { IEntityLoadConfiguration } from "../contracts/querying/entity-load-configuration";
import {
    ComparisonOperator,
    CompositeFilterOperator,
    CompositeQueryFilter,
    DefinitionQueryFilter,
    FilterDataType,
    PropertyQueryFilter,
    RelationQueryFilter,
} from "../contracts/querying/filters";
import { Query } from "../contracts/querying/query";
import { InvalidOperationError } from "../errors/invalid-operation-error";
import Guard from "../guard";
import SettingResource from "../models/setting-resource";
import { IExtendedContentHubClient } from "./extended-client";

/**
 * Client for fetching setting and category entities.
 */
export interface ISettingsClient {
    /**
     * Gets the id of a category by name.
     * @param name - cannot be an empty or whitespace string
     * @returns Id of the category entity.
     */
    getCategoryIdAsync(name: string): NullableResultPromise<number>;

    /**
     * Gets the setting with the specified name from the specified category.
     * @param category - The category to get the setting from
     * @param name - The name of the setting to get
     * @param loadConfiguration - Load configuration
     * @returns The setting with the specified name from the specified category or null if that setting doesn't exist.
     */
    getSettingAsync(
        category: string,
        name: string,
        loadConfiguration?: Nullable<IEntityLoadConfiguration>
    ): NullableResultPromise<IEntity>;

    /**
     * Gets the setting value and schema for the desired setting name in the specified category.
     * @param category - The category to get the setting from
     * @param name - The name of the setting to get
     */
    getSettingValueAndSchemaAsync(category: string, name: string): NullableResultPromise<SettingResource>;

    /**
     * Gets the settings for the specified category.
     * @param category - The category to get the setting for
     * @param loadConfiguration - Load configuration
     * @returns A list of all setting entities for the specified category.
     */
    getSettingsForCategoryAsync(
        category: string,
        loadConfiguration?: Nullable<IEntityLoadConfiguration>
    ): Promise<Array<IEntity>>;
}

export class SettingsClient implements ISettingsClient {
    private readonly _client: IExtendedContentHubClient;

    constructor(client: IExtendedContentHubClient) {
        Guard.notNull(client);
        this._client = client;
    }

    async getCategoryIdAsync(name: string): NullableResultPromise<number> {
        Guard.stringNotNullOrEmpty(name);

        // Build query
        const query = new Query();

        query.filter = new CompositeQueryFilter({
            combineMethod: CompositeFilterOperator.And,
            children: [
                new DefinitionQueryFilter({
                    name: DEFINITIONS["SettingsCategory"].definitionName,
                    operator: ComparisonOperator.Equals,
                }),
                new PropertyQueryFilter({
                    operator: ComparisonOperator.Contains,
                    dataType: FilterDataType.String,
                    property: DEFINITIONS["SettingsCategory"].name,
                    value: name,
                }),
            ],
        });

        // Get the category
        const result = await this._client.querying.queryIdsAsync(query);

        let id: Nullable<number> = null;
        if (!result.items || !result.items.length) {
            return null;
        } else if (result.items.length === 1) {
            id = result.items[0];
            return id;
        } else {
            throw new InvalidOperationError("Can't have more than one result, must be unique");
        }
    }

    async getSettingAsync(
        category: string,
        name: string,
        loadConfiguration: Nullable<IEntityLoadConfiguration> = null
    ): NullableResultPromise<IEntity> {
        Guard.stringNotNullOrEmpty(category);
        Guard.stringNotNullOrEmpty(name);

        // Get the category id
        const categoryId = await this.getCategoryIdAsync(category);
        if (categoryId == null) {
            return null;
        }

        // Build query
        const query = new Query();

        query.filter = new CompositeQueryFilter({
            combineMethod: CompositeFilterOperator.And,
            children: [
                new DefinitionQueryFilter({
                    name: DEFINITIONS["Setting"].definitionName,
                    operator: ComparisonOperator.Equals,
                }),
                new PropertyQueryFilter({
                    operator: ComparisonOperator.Contains,
                    dataType: FilterDataType.String,
                    property: DEFINITIONS["Setting"].name,
                    value: name,
                }),
                new RelationQueryFilter({
                    relation: DEFINITIONS["Setting"].settingCategoryToSettings,
                    parentId: categoryId,
                }),
            ],
        });

        // Get the setting
        const result = await this._client.querying.queryAsync(query, loadConfiguration);
        return result.items[0] ?? null;
    }

    async getSettingValueAndSchemaAsync(category: string, name: string): NullableResultPromise<SettingResource> {
        Guard.stringNotNullOrEmpty(category);
        Guard.stringNotNullOrEmpty(name);

        const link = await this._client.linkHelper.settingByNameLink(category, name);

        // Get the setting
        const result = await this._client.raw.getAsync<SettingResource>(link.href);

        ResponseHandler.handleErrors(result);

        return result.content!;
    }

    async getSettingsForCategoryAsync(
        category: string,
        loadConfiguration: Nullable<IEntityLoadConfiguration> = null
    ): Promise<Array<IEntity>> {
        Guard.stringNotNullOrEmpty(category);

        // Get the category id
        const categoryId = await this.getCategoryIdAsync(category);
        if (categoryId == null) {
            throw new InvalidOperationError("categoryId was not found");
        }

        // Build query
        const query = new Query();

        query.filter = new CompositeQueryFilter({
            combineMethod: CompositeFilterOperator.And,
            children: [
                new DefinitionQueryFilter({
                    name: DEFINITIONS["Setting"].definitionName,
                    operator: ComparisonOperator.Equals,
                }),
                new RelationQueryFilter({
                    relation: DEFINITIONS["Setting"].settingCategoryToSettings,
                    parentId: categoryId,
                }),
            ],
        });

        // Get the settings
        const result = await this._client.querying.queryAsync(query, loadConfiguration);
        return result.items;
    }
}
